import { useEffect } from "react";
import type { ErrorComponentProps } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { createLogger } from "../../logger";
import { Button } from "../ui/Button";

const log = createLogger("core:content-error");

/**
 * What a screen shows when its own route fails to load or render. Registered as the router's
 * default error component, so a failing page appears INSIDE the frame that wraps it (the sidebar
 * and top bar stay) instead of replacing the whole screen. Logged through the @core logger.
 */
export function ContentError({ error, reset }: ErrorComponentProps) {
  const { t } = useTranslation("common");

  useEffect(() => {
    log.error("Route failed to load or render", {
      context: "route-error",
      error: error.message,
    });
  }, [error]);

  return (
    <div
      role="alert"
      className="flex flex-col items-start gap-md rounded-lg border border-(--border-default) bg-surface-card p-xl"
    >
      <div className="flex flex-col gap-xs">
        <h1 className="text-lg font-semibold text-(--text-primary)">
          {t("layout.content-error-title")}
        </h1>
        <p className="text-sm-regular text-(--text-secondary)">
          {t("layout.content-error-description")}
        </p>
      </div>
      <Button variant="secondary" onPress={reset}>
        {t("actions.retry")}
      </Button>
    </div>
  );
}
